// InspecDoor Historie Helper Functions
// /lib/history-helpers.ts  
// Version: 1.1 - Kompatibel mit V1 und V3
// Datum: 11.09.2025 / Update: 17.09.2025

import { getGermanTime } from './timezone-helpers'

// Typen für Historie-Events
export enum HistoryEventType {
  // Wartung
  WARTUNG_BEGONNEN = 'wartung_begonnen',
  WARTUNG_GESPEICHERT = 'wartung_gespeichert',
  WARTUNG_ABGESCHLOSSEN = 'wartung_abgeschlossen',
  
  // Mängel
  MANGEL_FESTGESTELLT = 'mangel_festgestellt',
  MANGEL_BEHOBEN = 'mangel_behoben',
  MANGEL_BESEITIGT = 'mangel_beseitigt',
  MANGEL_KONNTE_NICHT_BESEITIGT_WERDEN = 'mangel_konnte_nicht_beseitigt_werden',
  
  // Schäden
  SCHADEN_FESTGESTELLT = 'schaden_festgestellt',
  SCHADEN_BEHOBEN = 'schaden_behoben',
  
  // Reparaturen
  REPARATUR_ANGEORDNET = 'reparatur_angeordnet',
  REPARATUR_AUSGEFUEHRT = 'reparatur_ausgefuehrt',
  REPARATUR_STORNIERT = 'reparatur_storniert',
  
  // Fotos
  FOTO_HINZUGEFUEGT = 'foto_hinzugefuegt',
  FOTO_GELOESCHT = 'foto_geloescht'
}

// Generische Funktion zum Erstellen eines Historie-Eintrags
export async function createHistoryEntry(
  supabase: any,
  data: {
    doorId: string,
    eventType: HistoryEventType,
    eventData: any,
    userId?: string,
    protocolId?: string,
    inspectionId?: string
  }
) {
  const { error } = await supabase.from('door_history').insert({
    door_id: data.doorId,
    event_type: data.eventType,
    event_data: data.eventData,
    event_date: getGermanTime('iso'),
    user_id: data.userId,
    protocol_id: data.protocolId,
    inspection_id: data.inspectionId
  });

  if (error) {
    console.error('Error creating history entry:', error);
    throw error;
  }
}

// Wartung begonnen
export async function addWartungBegonnen(
  supabase: any,
  doorId: string,
  inspector: string,
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {



  const eventData = {
    inspector,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.WARTUNG_BEGONNEN,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Wartung gespeichert (mit Unterschrift)
export async function addWartungGespeichert(
  supabase: any,
  doorId: string,
  inspector: string,
  userId?: string,
  inspectionId?: string
) {
  const eventData = {
    inspector,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.WARTUNG_GESPEICHERT,
    eventData,
    userId,
    inspectionId
  });
}

// Mangel festgestellt - Mit TypeScript-Trick für beide Versionen
export async function addMangelFestgestellt(
  ...args: any[]
): Promise<any> {
  const supabase = args[0];
  const doorId = args[1];
  const parameter = args[2];
  const description = args[3];
  const material = args[4] || "";
  
  let photos: string[] = [];
  let userId: string | undefined;
  let protocolId: string | undefined;
  let inspectionId: string | undefined;
  let repairId: string | undefined;
  
  if (args.length >= 11) {
    // V1 Format (11+ Parameter)
    photos = args[7] || [];
    userId = args[9];
    protocolId = args[10];
    inspectionId = args[11];
    repairId = args[12];  // NEU: repair_id
  } else {
    // V3 Format (10 Parameter)
    photos = args[5] || [];
    userId = args[6];
    protocolId = args[7];
    inspectionId = args[8];
  }
  
  const eventData = {
    parameter,
    description,
    material,
    photos,
    timestamp: getGermanTime('iso'),
    ...(repairId && { repair_id: repairId }),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.MANGEL_FESTGESTELLT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Mangel beseitigt (vor Ort)
export async function addMangelBeseitigt(
  supabase: any,
  doorId: string,
  parameter: string,
  description: string,
  material: string = "",
  timeInMinutes: number = 0,
  photos: string[] = [],
  userId?: string,
  protocolId?: string,
  inspectionId?: string,
  zeitGeselle?: number,  // FEAT-02: Separate Zeiterfassung
  zeitLehrling?: number  // FEAT-02: Separate Zeiterfassung
) {
  const eventData = {
    parameter,
    description,
    material,
    timeInMinutes,
    zeitGeselle: zeitGeselle ?? 0,  // FEAT-02
    zeitLehrling: zeitLehrling ?? 0,  // FEAT-02
    photos,
    timestamp: getGermanTime('iso'),
    };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.MANGEL_BESEITIGT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Mangel konnte nicht beseitigt werden
export async function addMangelKonnteNichtBeseitigtWerden(
  supabase: any,
  doorId: string,
  parameter: string,
  description: string,
  grund: string,
  photos: string[] = [],
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    parameter,
    description,
    grund,
    photos,
    timestamp: getGermanTime('iso'),
    };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.MANGEL_KONNTE_NICHT_BESEITIGT_WERDEN,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Schaden festgestellt
export async function addSchadenFestgestellt(
  supabase: any,
  doorId: string,
  parameter: string,  // BUG W FIX: Parameter hinzugefügt für Duplikat-Check
  description: string,
  material: string = "",
  requiresOffer: boolean = false,
  photos: string[] = [],
  immediateAction?: boolean,
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {

  // BUG W FIX: Prüfe ob bereits ein Event für diesen Parameter existiert
  const { data: existingEvents } = await supabase
    .from('door_history')
    .select('*')
    .eq('door_id', doorId)
    .eq('event_type', 'schaden_festgestellt')
    .filter('event_data->>parameter', 'eq', parameter);

  if (existingEvents && existingEvents.length > 0) {
    console.log('⚠️ [BUG W FIX] Skipping duplicate SCHADEN_FESTGESTELLT event for parameter:', parameter);
    return { success: true, skipped: true };
  }

  // Zusätzlich: Prüfe ob bereits ein identisches Event existiert (innerhalb der letzten 5 Sekunden)
  const fiveSecondsAgo = new Date(Date.now() - 5000).toISOString();
  const { data: recentEvents } = await supabase
    .from('door_history')
    .select('*')
    .eq('door_id', doorId)
    .eq('event_type', 'schaden_festgestellt')
    .gte('created_at', fiveSecondsAgo)
    .order('created_at', { ascending: false });

  if (recentEvents && recentEvents.length > 0) {
    // Prüfe ob es ein identisches Event gibt
    const duplicate = recentEvents.find((e: any) =>
      e.event_data?.description === description ||
      e.event_data?.parameter === parameter
    );

    if (duplicate) {
      console.log('⚠️ Skipping duplicate SCHADEN_FESTGESTELLT event (time-based)');
      return { success: true, skipped: true };
    }
  }

  const eventData = {
    parameter,  // BUG W FIX: Parameter in event_data speichern
    description,
    material,
    requiresOffer,
    immediateAction,
    photos,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.SCHADEN_FESTGESTELLT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Schaden behoben (vor Ort)
export async function addSchadenBehoben(
  supabase: any,
  doorId: string,
  description: string,
  material: string = "",
  timeInMinutes: number = 0,
  cost: number = 0,
  photos: string[] = [],
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    description,
    material,
    timeInMinutes,
    cost,
    photos,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.SCHADEN_BEHOBEN,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Wartung abgeschlossen - Flexibel für beide Versionen
export async function addWartungAbgeschlossen(
  ...args: any[]
): Promise<any> {
  const supabase = args[0];
  const doorId = args[1];
  
  let inspector: string;
  let summary: any;
  let userId: string | undefined;
  let protocolId: string | undefined;
  let inspectionId: string | undefined;
  let repairId: string | undefined;
  
  if (typeof args[2] === 'string') {
    // V3 Format
    inspector = args[2];
    summary = args[3] || { totalParameters: 0, remarks: "Keine Bemerkungen" };
    userId = args[4];
    protocolId = args[5];
    inspectionId = args[6];
  } else {
    // V1 Format  
    inspector = "System";
    summary = {
      status: args[2],
      totalParameters: args[3] || 0,
      timeSpent: args[4] || 0,
      remarks: args[5] || "Keine Bemerkungen"
    };
    userId = args[6];
    protocolId = args[7];
    inspectionId = args[8];
  }
  
  const eventData = {
    inspector,
    summary,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.WARTUNG_ABGESCHLOSSEN,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Foto hinzugefügt
export async function addFotoHinzugefuegt(
  supabase: any,
  doorId: string,
  photoUrl: string,
  category: string = 'allgemein',
  description: string = '',
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    photoUrl,
    category,
    description,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.FOTO_HINZUGEFUEGT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Foto gelöscht
export async function addFotoGeloescht(
  supabase: any,
  doorId: string,
  photoUrl: string,
  reason: string = '',
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    photoUrl,
    reason,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.FOTO_GELOESCHT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Reparatur angeordnet
export async function addReparaturAngeordnet(
  supabase: any,
  doorId: string,
  repairId: string,
  description: string,
  orderedBy: string,
  priority: string = 'medium',
  photos: string[] = [],
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    repairId,
    description,
    orderedBy,
    priority,
    photos,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.REPARATUR_ANGEORDNET,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Reparatur ausgeführt
export async function addReparaturAusgefuehrt(
  supabase: any,
  doorId: string,
  repairId: string,
  description: string,
  executedBy: string,
  material: string = '',
  timeInMinutes: number = 0,
  cost: number = 0,
  photos: string[] = [],
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    repairId,
    description,
    executedBy,
    material,
    timeInMinutes,
    cost,
    photos,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.REPARATUR_AUSGEFUEHRT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}

// Reparatur storniert
export async function addReparaturStorniert(
  supabase: any,
  doorId: string,
  repairId: string,
  reason: string,
  cancelledBy: string,
  userId?: string,
  protocolId?: string,
  inspectionId?: string
) {
  const eventData = {
    repairId,
    reason,
    cancelledBy,
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.REPARATUR_STORNIERT,
    eventData,
    userId,
    protocolId,
    inspectionId
  });
}


  export async function addSchadenGemeldet(
    supabase: any,
    doorId: string,
    parameterId: string,
    description: string,
    material?: string
  ) {
    return await supabase
      .from('door_history')
      .insert({
        door_id: doorId,
        event_type: 'SCHADEN_GEMELDET',
        event_data: {
          parameter_id: parameterId,
          description,
          material,
          status: 'gemeldet'
        }
      });
  }

// GRUPPE 2: V4-spezifische Historie-Funktionen

// BUG 5 FIX: Helper um Duplikate zu prüfen
async function checkDuplicateHistoryEntry(
  supabase: any,
  doorId: string,
  eventType: HistoryEventType,
  inspectionId?: string,
  parameterId?: string
): Promise<boolean> {
  const query = supabase
    .from('door_history')
    .select('id')
    .eq('door_id', doorId)
    .eq('event_type', eventType);

  if (inspectionId) {
    query.eq('inspection_id', inspectionId);
  }
  if (parameterId) {
    query.contains('event_data', { parameter_id: parameterId });
  }

  const { data } = await query.limit(1);
  return data && data.length > 0;
}

// Mangel behoben (V4) - BUG 3+4+5 FIX: Mit Fotos und Duplikat-Prüfung
export async function addMangelBehobenV4(
  supabase: any,
  doorId: string,
  inspectionId: string,
  parameterId: string,
  paramLabel: string,
  description: string,
  photos: string[] = []  // BUG 4 FIX: Nachher-Fotos separat speichern
) {
  // BUG 5 FIX: Duplikat-Prüfung
  const isDuplicate = await checkDuplicateHistoryEntry(
    supabase, doorId, HistoryEventType.MANGEL_BEHOBEN, inspectionId, parameterId
  );
  if (isDuplicate) {
    console.log('⚠️ Skipping duplicate mangel_behoben entry for parameter:', parameterId);
    return { success: true, skipped: true };
  }

  const eventData = {
    parameter_id: parameterId,
    parameter_label: paramLabel,
    description,
    photos,  // BUG 4 FIX: Nachher-Fotos diesem Eintrag zuordnen
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.MANGEL_BEHOBEN,
    eventData,
    inspectionId
  });
}

// Schaden behoben (V4) - BUG 3+4+5 FIX: Mit Fotos und Duplikat-Prüfung
export async function addSchadenBehobenV4(
  supabase: any,
  doorId: string,
  inspectionId: string,
  parameterId: string,
  paramLabel: string,
  description: string,
  photos: string[] = []  // BUG 4 FIX: Nachher-Fotos separat speichern
) {
  // BUG 5 FIX: Duplikat-Prüfung
  const isDuplicate = await checkDuplicateHistoryEntry(
    supabase, doorId, HistoryEventType.SCHADEN_BEHOBEN, inspectionId, parameterId
  );
  if (isDuplicate) {
    console.log('⚠️ Skipping duplicate schaden_behoben entry for parameter:', parameterId);
    return { success: true, skipped: true };
  }

  const eventData = {
    parameter_id: parameterId,
    parameter_label: paramLabel,
    description,
    photos,  // BUG 4 FIX: Nachher-Fotos diesem Eintrag zuordnen
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.SCHADEN_BEHOBEN,
    eventData,
    inspectionId
  });
}

// Wartung abgeschlossen (V4)
export async function addWartungAbgeschlossenV4(
  supabase: any,
  doorId: string,
  inspectionId: string,
  inspector?: string
) {
  const eventData = {
    inspector: inspector || 'System',
    timestamp: getGermanTime('iso'),
  };

  return await createHistoryEntry(supabase, {
    doorId,
    eventType: HistoryEventType.WARTUNG_ABGESCHLOSSEN,
    eventData,
    inspectionId
  });
}

